import React, { useCallback } from "react";
import {
  SelectInput,
  BooleanInput,
  FormDataConsumerRenderParams,
  required,
  useTranslate,
} from "react-admin";
import { NotificationTypeDto } from "../../../common/data/service-proxies";
import { isEmpty } from "lodash";
import { from } from "manipula";

interface Props extends FormDataConsumerRenderParams {
  subject: string;
  eventTypes: Array<NotificationTypeDto> | null;
}

export const NotificationEventTypeSelector = ({
  subject,
  eventTypes,
  formData,
  scopedFormData,
  getSource,
  ...props
}: Props) => {
  const translate = useTranslate();

  const getUnassignedEventTypes = useCallback(() => {
    if (!eventTypes) {
      return [];
    }

    const selectedEventTypesFormData = formData["eventPreferences"][subject] as
      | Array<{ eventId: number; urgent?: boolean } | undefined>
      | undefined;

    if (!selectedEventTypesFormData) {
      return from(
        eventTypes.map((x) => ({
          id: x.id,
          name: translate(
            `resources.admin-notifications.notificationTypes.${subject}.${x.name}`,
            { _: x.name }
          ),
        }))
      )
        .orderBy((x) => x.name)
        .toArray();
    }

    const selectedEventTypes = selectedEventTypesFormData.map(
      (x) => x && x.eventId
    );

    if (!selectedEventTypes || isEmpty(selectedEventTypes)) {
      return from(
        eventTypes.map((x) => ({
          id: x.id,
          name: translate(
            `resources.admin-notifications.notificationTypes.${subject}.${x.name}`,
            { _: x.name }
          ),
        }))
      )
        .orderBy((x) => x.name)
        .toArray();
    }
    const initial = eventTypes.map((c) => c.id!);
    const selected = new Set(selectedEventTypes);
    const remaining = new Set(
      scopedFormData?.eventId
        ? [...initial.filter((x) => !selected.has(x)), scopedFormData.eventId]
        : initial.filter((x) => !selected.has(x))
    );

    return from(
      eventTypes
        .filter((x) => remaining.has(x.id!))
        .map((x) => ({
          id: x.id,
          name: translate(
            `resources.admin-notifications.notificationTypes.${subject}.${x.name}`,
            { _: x.name }
          ),
        }))
    )
      .orderBy((x) => x.name)
      .toArray();
  }, [eventTypes, formData, scopedFormData, subject, translate]);

  if (!getSource || !eventTypes) {
    return null;
  }

  return (
    <>
      <SelectInput
        {...props}
        label="resources.admin-notifications.labels.eventType"
        source={getSource("eventId")}
        choices={getUnassignedEventTypes()}
        validate={required()}
        translate="no"
        variant="outlined"
      />
      <BooleanInput
        {...props}
        source={getSource("urgent")}
        label="resources.admin-notifications.labels.urgent"
      />
    </>
  );
};
